/* eslint-disable @eslint-react/dom-no-dangerously-set-innerhtml */
import { useFieldApi } from '@data-driven-forms/react-form-renderer';
import styled from '@emotion/styled';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormElementWidgetWithOptions } from 'lib-node-amb-renderer';
import { useEffect, useMemo, useRef, useState } from 'react';

import theme from '~/theme/';
import palette from '~/theme/mui/palette';

import { validationError } from '../utils';

const CustomSelectWithStyledOptionsWrapper = styled.div<any>`
  box-sizing: border-box;
  position: relative;
  margin-bottom: 1rem;
`;

const CustomSelectWithStyledOptionsButton = styled.button<any>`
  width: 100%;
  height: auto;
  background-color: ${theme.colors?.white};
  border-style: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  border-color: ${({ invalid }) =>
    invalid ? theme.colors?.red[500] : theme.colors?.gray[200]};
  border-width: ${({ invalid }) => (invalid ? '2px' : '1px')};
  padding: 0.4rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CustomSelectWithStyledOptionsDrawer = styled<any>('div')`
  width: 100%;
  height: ${({ isOpen }) => (isOpen ? 'auto' : '0')};
  overflow: hidden;
  position: absolute;
  transition: height 0.3s;
  z-index: ${({ isOpen }) => (isOpen ? '1' : '-1')};
  padding-bottom: 1rem;
`;

const DrawerWrapper = styled<any>('ul')`
  text-decoration: none;
  border: 1px solid #e2e8f0;
  width: 100%;
  max-height: ${(props) => (props.hasMaxHeight ? '300px' : 'auto')};
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #ffffff;
`;

const StyledOption = styled<any>('li')`
  border-bottom: 1px solid #e2e8f0;
  width: 100%;
  position: relative;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  margin: 0;
  &:hover {
    background-color: ${theme.colors?.gray[50]};
  }
`;

const StyledRadioInput = styled.input<any>`
  position: absolute;
  left: 0;
  opacity: 0;
`;

const Placeholder = styled.p<any>`
  color: ${theme.colors?.bgGray[800]};
`;

const StyledErrorMessage = styled.p<any>`
  color: ${palette.error.main};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

export class CustomSelectWithStyledOptionsClass extends FormElementWidgetWithOptions {
  icon;
  required;

  constructor(props) {
    super(props);
    this.icon = props.icon ?? faChevronDown;
  }
}

type CustomSelectWithStyledOptionsIconProps = {
  isOpen?: boolean;
  icon?: IconProp;
  hasSelection?: boolean;
};

function CustomSelectWithStyledOptionsIcon({
  isOpen,
  icon,
  hasSelection,
}: Readonly<CustomSelectWithStyledOptionsIconProps>) {
  const iconStyles = {
    fontSize: '.65rem',
    rotate: isOpen ? '-180deg' : '0deg',
    transition: 'rotate 0.3s',
  };

  return (
    <FontAwesomeIcon
      color={hasSelection ? theme.colors?.black : theme.colors?.bgGray[800]}
      icon={icon || faChevronDown}
      style={iconStyles}
    />
  );
}

type StyledOptionsProps = {
  label?: string;
  value?: string;
  handleChange?: (...args: any[]) => any;
  name?: string;
  isDisabled?: boolean;
  optionStyles?: any;
};

function StyledOptions({
  label,
  value,
  name,
  isDisabled = false,
  handleChange,
  optionStyles = {},
}: Readonly<StyledOptionsProps>) {
  return (
    <StyledOption
      value={value}
      onClick={() => {
        if (isDisabled) return;
        handleChange(value);
      }}
      disabled={isDisabled}
      style={optionStyles}
    >
      <label htmlFor={name}>
        <StyledRadioInput type="radio" value={value} name={name} />
        <span dangerouslySetInnerHTML={{ __html: label }} />
      </label>
    </StyledOption>
  );
}

type CustomSelectWithStyledOptionsProps = {
  name: string;
  value?: any;
  icon?: string;
  Children?: React.ReactNode;
  options?: any[];
  renderLabel?: (...args: any[]) => any;
  sx?: any;
  onChange?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  placeholder?: string;
  matchType?: number;
  isClearable?: boolean;
};

export default function CustomSelectWithStyledOptions(
  props: Readonly<CustomSelectWithStyledOptionsProps>,
) {
  const {
    name,
    options,
    icon,
    input,
    label,
    placeholder = 'Select an option',
    meta,
    validateOnMount,
    hasMaxHeight = false,
    selectedOptionStyle = {},
  } = useFieldApi({
    ...props,
    type: 'select',
  });
  const { value, onChange, onBlur } = input;

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const selectedOption = useMemo(() => {
    if (!value) return <Placeholder>{placeholder}</Placeholder>;
    const findOption = options.find((option) => option.value === value);
    return <p dangerouslySetInnerHTML={{ __html: findOption.label }}></p>;
  }, [options, value, placeholder]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const listBox = document.getElementById('listBox');
      listBox.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isOpen]);

  const hasError = validationError(meta, validateOnMount);
  const text = hasError || ((meta.touched || validateOnMount) && meta.warning);

  const handleChange = (newValue) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const handleClickOutside = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setIsOpen(false);
    }
  };
  const handleFocus = () => {
    onChange(value);
    setIsOpen(true);
  };

  return (
    <div>
      <CustomSelectWithStyledOptionsWrapper
        className="CompositeDropdownSelect"
        name={name}
        ref={ref}
        onBlur={() => onBlur(value)}
        onFocus={() => handleFocus()}
      >
        {label ?? <p>{label}</p>}
        <CustomSelectWithStyledOptionsButton
          className="CompositeDropdownSelect__value"
          invalid={hasError}
          role="combobox"
          aria-labelledby="select button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls="select-dropdown"
        >
          <div style={selectedOptionStyle}>{selectedOption}</div>
          <CustomSelectWithStyledOptionsIcon
            isOpen={isOpen}
            icon={icon ?? ''}
            hasSelection={value}
          />
        </CustomSelectWithStyledOptionsButton>
        <CustomSelectWithStyledOptionsDrawer
          className="CompositeDropdownSelect__children"
          id="listBox"
          isOpen={isOpen}
        >
          <DrawerWrapper
            hasMaxHeight={hasMaxHeight}
            role="listbox"
            id="select-dropdown"
          >
            {options.map((option, index) => (
              <StyledOptions
                key={option.value}
                name={index}
                value={option.value}
                isDisabled={option.isDisabled}
                label={option.label}
                handleChange={(newValue) => handleChange(newValue)}
              />
            ))}
          </DrawerWrapper>
        </CustomSelectWithStyledOptionsDrawer>
        {!!hasError && <StyledErrorMessage>{text}</StyledErrorMessage>}
      </CustomSelectWithStyledOptionsWrapper>
    </div>
  );
}
